import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    TextInput,
    NumberInput,
    Select,
    Alert,
    Loader,
    Badge,
    Checkbox,
    Divider,
    Modal
} from '@mantine/core';
import {
    IconReceiptTax,
    IconCheck,
    IconAlertCircle,
    IconDownload,
    IconMail,
    IconFileInvoice,
    IconCalendar
} from '@tabler/icons-react';
import CardModal from '@/components/card-modal';
import DatePickerCustom from '@/components/custom-date-picker';
import dayjs from 'dayjs';
import { useQueryClient } from '@tanstack/react-query';
import { Reservation, ServiceBooking } from '@turizi/shared';
import { localTenant } from '@/services/session-service';
import { useGenerateAfipInvoice, useReservation, reservationQueries } from '@/hooks/api-hooks';
import SendAfipVoucherEmailModal, { SendAfipVoucherEmailTarget } from '@/components/send-afip-voucher-email-modal';
import { notify } from '@/lib/notify';
import { formatPrice, formatDayShort } from '@/lib/format-utils';

interface Props {
    opened: boolean;
    onClose: () => void;
    reservation?: Reservation;
}

interface ReceiptGroupBooking extends ServiceBooking {
    invoiceAmount?: number | string;
}

interface ReceiptGroup {
    key: string;
    pos: number;
    taxPercentage: number;
    ivaRate: string;
    bookings: ReceiptGroupBooking[];
    totalAmount: number | string;
    netAmount: number;
    ivaAmount: number;
    status: 'idle' | 'generating' | 'success' | 'error';
    error?: string | null;
    result?: {
        voucherNumber: number;
        CAE: string;
        CAEFchVto: string;
        voucherId: number;
    } | null;
}

const getIvaInfoFromTaxPercentage = (pct: number): { ivaId: number; ivaRate: string; rate: number } => {
    if (Math.abs(pct - 10.5) < 0.1) return { ivaId: 4, ivaRate: '4', rate: 0.105 };
    if (Math.abs(pct - 27) < 0.1) return { ivaId: 6, ivaRate: '6', rate: 0.27 };
    if (pct === 0) return { ivaId: 3, ivaRate: '3', rate: 0 };
    return { ivaId: 5, ivaRate: '5', rate: 0.21 };
};

export default function GenerateReservationTaxReceiptsModal({
    opened,
    onClose,
    reservation
}: Props) {
    const { t } = useTranslation();
    const tenant = localTenant();
    const queryClient = useQueryClient();
    const generateInvoiceMutation = useGenerateAfipInvoice();

    // Fetch full reservation if service_bookings is missing
    const { data: fetchedReservationRes, isLoading: isLoadingReservation } = useReservation(
        reservation?.id || 0
    );
    const fullReservation: Reservation | undefined = (fetchedReservationRes as any)?.data || reservation;

    const afipSettings: any = tenant?.settings?.afip;
    const isAfipConfigured = !!(afipSettings?.cuit && afipSettings?.cert && afipSettings?.key);

    const rawSalesPoints: any[] = Array.isArray(afipSettings?.sales_points) && afipSettings.sales_points.length > 0
        ? afipSettings.sales_points
        : Array.isArray(afipSettings?.all_sales_points) && afipSettings.all_sales_points.length > 0
            ? afipSettings.all_sales_points
            : (afipSettings?.pos_number ? [{ number: afipSettings.pos_number, name: afipSettings.pos_name || 'Turizi POS', system: afipSettings.pos_system || '' }] : []);

    let posSelectData = rawSalesPoints.map((p: any) => {
        const numStr = String(p.number ?? p.numero ?? p.Nro ?? p.id ?? '').trim();
        const nameStr = String(p.name || p.displayName || p.nombreFantasia || p.Nombre || '').trim();
        const sysStr = String(p.system || p.sistema || p.EmisionTipo || p.Sistema || '').trim();
        const label = `POS ${numStr}${nameStr ? ` - ${nameStr}` : ''}${sysStr ? ` (${sysStr})` : ''}`;
        return { value: numStr, label };
    }).filter((item) => item.value !== '' && item.value !== 'undefined' && item.value !== 'null');


    const defaultPos = rawSalesPoints.length > 0
        ? Number(rawSalesPoints[0].number || 1)
        : (afipSettings?.pos_number ? Number(afipSettings.pos_number) : 1);

    // Common Client/Receiver state
    const [receiverName, setReceiverName] = useState('');
    const [docTipo, setDocTipo] = useState('99');
    const [docNro, setDocNro] = useState('0');
    const [taxCondition, setTaxCondition] = useState('5');
    const [invoiceType, setInvoiceType] = useState('6');
    const [concepto, setConcepto] = useState('2');
    const [description, setDescription] = useState('');
    const [fchServDesde, setFchServDesde] = useState<Date | null>(new Date());
    const [fchServHasta, setFchServHasta] = useState<Date | null>(new Date());
    const [fchVtoPago, setFchVtoPago] = useState<Date | null>(new Date());

    // Grouped receipts state
    const [groups, setGroups] = useState<ReceiptGroup[]>([]);
    const [isGeneratingAll, setIsGeneratingAll] = useState(false);

    // Email voucher state
    const [selectedVoucherForEmail, setSelectedVoucherForEmail] = useState<SendAfipVoucherEmailTarget | null>(null);

    const availablePosOptions = useMemo(() => {
        const list = [...posSelectData];
        groups.forEach((g) => {
            if (g.pos && !list.some((p) => p.value === String(g.pos))) {
                list.push({
                    value: String(g.pos),
                    label: `POS ${g.pos}`
                });
            }
        });
        return list;
    }, [posSelectData, groups]);

    // Initialize receiver state from reservation client
    useEffect(() => {
        if (!opened || !fullReservation) return;

        const client = fullReservation.client_business;
        if (client) {
            setReceiverName(client.tax_name || client.name || '');
            const detectedTaxCond = client.tax_condition || '5';
            setTaxCondition(detectedTaxCond);

            const hasCuit = client.tax_id_type === 'CUIT' || (client.tax_id_number && client.tax_id_number.replace(/\D/g, '').length === 11);
            if (hasCuit) {
                setDocTipo('80');
                setDocNro(client.tax_id_number?.replace(/\D/g, '') || '');
                setInvoiceType(detectedTaxCond === '1' ? '1' : '6');
            } else if (client.tax_id_number) {
                setDocTipo('96'); // DNI
                setDocNro(client.tax_id_number.replace(/\D/g, ''));
                setInvoiceType('6');
            } else {
                setDocTipo('99');
                setDocNro('0');
                setInvoiceType('6');
            }
        } else {
            setReceiverName('Consumidor Final');
            setDocTipo('99');
            setDocNro('0');
            setTaxCondition('5');
            setInvoiceType('6');
        }

        if (fullReservation.start_day) {
            setFchServDesde(new Date(fullReservation.start_day + 'T00:00:00'));
        }
        if (fullReservation.end_day) {
            setFchServHasta(new Date(fullReservation.end_day + 'T00:00:00'));
        }
        setFchVtoPago(new Date());
    }, [opened, fullReservation]);

    // Handle docTipo = 99
    useEffect(() => {
        if (docTipo === '99') {
            setDocNro('0');
            setTaxCondition('5');
        } else if (docNro === '0') {
            setDocNro('');
        }
    }, [docTipo]);

    // Group service bookings by POS and Tax Percentage
    useEffect(() => {
        if (!opened || !fullReservation) return;

        const bookings: ServiceBooking[] = fullReservation.service_bookings || [];
        const billableBookings = bookings.filter((b) => Number(b.money_in_amount || 0) > 0);

        const groupMap = new Map<string, {
            pos: number;
            taxPercentage: number;
            ivaRate: string;
            bookings: ReceiptGroupBooking[];
            totalAmount: number;
        }>();

        billableBookings.forEach((booking) => {
            const svc = booking.service || booking.service_data;
            const pos = svc?.pos != null && Number(svc.pos) > 0 ? Number(svc.pos) : defaultPos;
            const taxPct = svc?.tax_percentage != null ? Number(svc.tax_percentage) : 21;
            const { ivaRate } = getIvaInfoFromTaxPercentage(taxPct);

            const key = `${pos}_${ivaRate}`;
            const amount = Number(booking.money_in_amount || 0);

            if (!groupMap.has(key)) {
                groupMap.set(key, {
                    pos,
                    taxPercentage: taxPct,
                    ivaRate,
                    bookings: [{ ...booking, invoiceAmount: amount }],
                    totalAmount: amount
                });
            } else {
                const existing = groupMap.get(key)!;
                existing.bookings.push({ ...booking, invoiceAmount: amount });
                existing.totalAmount += amount;
            }
        });

        const initialGroups: ReceiptGroup[] = Array.from(groupMap.entries()).map(([key, data]) => {
            let rate = 0.21;
            if (data.ivaRate === '4') rate = 0.105;
            if (data.ivaRate === '6') rate = 0.27;
            if (data.ivaRate === '3') rate = 0;

            const netAmount = invoiceType === '11'
                ? data.totalAmount
                : Math.round((data.totalAmount / (1 + rate)) * 100) / 100;
            const ivaAmount = invoiceType === '11'
                ? 0
                : Math.round((data.totalAmount - netAmount) * 100) / 100;

            return {
                key,
                pos: data.pos,
                taxPercentage: data.taxPercentage,
                ivaRate: data.ivaRate,
                bookings: data.bookings,
                totalAmount: data.totalAmount,
                netAmount,
                ivaAmount,
                status: 'idle'
            };
        });

        setGroups(initialGroups);
    }, [opened, fullReservation?.id, defaultPos]);

    // Recalculate Net and IVA when invoiceType or group ivaRate/totalAmount changes
    const recalculateGroupAmounts = (group: ReceiptGroup, currentInvoiceType: string, overrideIvaRate?: string, overridePos?: number): ReceiptGroup => {
        const rateCode = overrideIvaRate ?? group.ivaRate;
        let rate = 0.21;
        if (rateCode === '4') rate = 0.105;
        if (rateCode === '6') rate = 0.27;
        if (rateCode === '3') rate = 0;

        const numericTotal = typeof group.totalAmount === 'number'
            ? group.totalAmount
            : (group.totalAmount === '' ? 0 : (parseFloat(String(group.totalAmount)) || 0));

        const netAmount = currentInvoiceType === '11'
            ? numericTotal
            : Math.round((numericTotal / (1 + rate)) * 100) / 100;
        const ivaAmount = currentInvoiceType === '11'
            ? 0
            : Math.round((numericTotal - netAmount) * 100) / 100;

        return {
            ...group,
            pos: overridePos !== undefined ? overridePos : group.pos,
            ivaRate: rateCode,
            netAmount,
            ivaAmount
        };
    };

    const handleUpdateInvoiceType = (newType: string) => {
        setInvoiceType(newType);
        setGroups((prev) => prev.map((g) => recalculateGroupAmounts(g, newType)));
    };

    const handleUpdateGroupPos = (index: number, newPos: number | null) => {
        if (!newPos) return;
        setGroups((prev) => {
            const next = [...prev];
            next[index] = recalculateGroupAmounts(next[index], invoiceType, undefined, newPos);
            return next;
        });
    };

    const handleUpdateGroupIvaRate = (index: number, newIvaRate: string) => {
        setGroups((prev) => {
            const next = [...prev];
            let newTaxPct = 21;
            if (newIvaRate === '4') newTaxPct = 10.5;
            if (newIvaRate === '6') newTaxPct = 27;
            if (newIvaRate === '3') newTaxPct = 0;

            next[index] = {
                ...recalculateGroupAmounts(next[index], invoiceType, newIvaRate),
                taxPercentage: newTaxPct
            };
            return next;
        });
    };

    const handleUpdateBookingAmount = (groupIndex: number, bookingIndex: number, val: number | string) => {
        setGroups((prev) => {
            const next = [...prev];
            const group = { ...next[groupIndex] };
            const newBookings = [...group.bookings];
            newBookings[bookingIndex] = {
                ...newBookings[bookingIndex],
                invoiceAmount: val
            };
            group.bookings = newBookings;

            // Recalculate totalAmount from all bookings in this group
            const newTotal = Math.round(newBookings.reduce((sum, b) => {
                const amt = typeof b.invoiceAmount === 'number'
                    ? b.invoiceAmount
                    : (b.invoiceAmount === '' ? 0 : (parseFloat(String(b.invoiceAmount)) || 0));
                return sum + amt;
            }, 0) * 100) / 100;

            group.totalAmount = newTotal;
            next[groupIndex] = recalculateGroupAmounts(group, invoiceType);
            return next;
        });
    };

    const handleUpdateGroupTotal = (groupIndex: number, val: number | string) => {
        setGroups((prev) => {
            const next = [...prev];
            const group = { ...next[groupIndex] };
            group.totalAmount = val;

            const numericTotal = typeof val === 'number'
                ? val
                : (val === '' ? 0 : (parseFloat(String(val)) || 0));

            if (group.bookings.length === 1) {
                group.bookings = [{
                    ...group.bookings[0],
                    invoiceAmount: val
                }];
            } else if (group.bookings.length > 1) {
                const currentTotal = group.bookings.reduce((sum, b) => {
                    const amt = typeof b.invoiceAmount === 'number'
                        ? b.invoiceAmount
                        : (b.invoiceAmount === '' ? 0 : (parseFloat(String(b.invoiceAmount)) || 0));
                    return sum + amt;
                }, 0);

                if (currentTotal > 0) {
                    let distributedSum = 0;
                    group.bookings = group.bookings.map((b, i) => {
                        if (i === group.bookings.length - 1) {
                            const lastAmt = Math.max(0, Math.round((numericTotal - distributedSum) * 100) / 100);
                            return { ...b, invoiceAmount: lastAmt };
                        }
                        const curAmt = typeof b.invoiceAmount === 'number'
                            ? b.invoiceAmount
                            : (b.invoiceAmount === '' ? 0 : (parseFloat(String(b.invoiceAmount)) || 0));
                        const distributed = Math.round((curAmt / currentTotal) * numericTotal * 100) / 100;
                        distributedSum += distributed;
                        return { ...b, invoiceAmount: distributed };
                    });
                } else {
                    const split = Math.round((numericTotal / group.bookings.length) * 100) / 100;
                    group.bookings = group.bookings.map((b) => ({
                        ...b,
                        invoiceAmount: split
                    }));
                }
            }

            next[groupIndex] = recalculateGroupAmounts(group, invoiceType);
            return next;
        });
    };

    // Generate single receipt
    const generateSingleReceipt = async (index: number, currentGroups = groups): Promise<{ success: boolean; voucherStr?: string }> => {
        if (!tenant) return { success: false };
        const group = currentGroups[index];
        if (!group || group.status === 'success') return { success: true };

        const numericTotal = typeof group.totalAmount === 'number'
            ? group.totalAmount
            : (group.totalAmount === '' ? 0 : (parseFloat(String(group.totalAmount)) || 0));

        if (numericTotal <= 0) {
            notify.error(t('amount_must_be_greater_than_zero'), t('error'));
            return { success: false };
        }

        if (!group.pos || group.pos <= 0) {
            notify.error(t('pos_required_error'), t('error'));
            return { success: false };
        }

        const cleanDocNro = docNro.replace(/\D/g, '');
        if (docTipo !== '99' && (!cleanDocNro || isNaN(parseInt(cleanDocNro, 10)) || parseInt(cleanDocNro, 10) <= 0)) {
            notify.error(t('invalid_doc_number'), t('error'));
            return { success: false };
        }

        if (invoiceType === '1' && docTipo !== '80') {
            notify.error(t('factura_a_requires_cuit'), t('error'));
            return { success: false };
        }

        // Set status to generating
        setGroups((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], status: 'generating', error: null };
            return next;
        });

        const payload = {
            PtoVta: group.pos,
            CbteTipo: parseInt(invoiceType, 10),
            Concepto: parseInt(concepto, 10),
            description: description.trim() || undefined,
            DocTipo: parseInt(docTipo, 10),
            DocNro: docTipo === '99' ? 0 : parseInt(cleanDocNro, 10) || 0,
            CondicionIVAReceptorId: docTipo === '99' ? 5 : parseInt(taxCondition, 10) || 5,
            receiver_name: receiverName || 'Consumidor Final',
            ImpTotal: numericTotal,
            ImpNeto: group.netAmount,
            ImpIVA: invoiceType === '11' ? 0 : group.ivaAmount,
            ivaId: invoiceType === '11' ? null : parseInt(group.ivaRate, 10),
            FchServDesde: (concepto === '2' || concepto === '3') && fchServDesde ? fchServDesde.toISOString().split('T')[0] : undefined,
            FchServHasta: (concepto === '2' || concepto === '3') && fchServHasta ? fchServHasta.toISOString().split('T')[0] : undefined,
            FchVtoPago: (concepto === '2' || concepto === '3') && fchVtoPago ? fchVtoPago.toISOString().split('T')[0] : undefined,
            business_id: fullReservation?.client_business?.id,
            reservation_id: Number(fullReservation?.id || reservation?.id)
        };

        try {
            const res: any = await generateInvoiceMutation.mutateAsync({
                tenantId: tenant.id,
                data: payload
            });

            const voucherFormatted = `${String(group.pos).padStart(4, '0')}-${String(res.voucherNumber).padStart(8, '0')}`;

            setGroups((prev) => {
                const next = [...prev];
                next[index] = {
                    ...next[index],
                    status: 'success',
                    error: null,
                    result: {
                        voucherNumber: res.voucherNumber,
                        CAE: res.CAE,
                        CAEFchVto: res.CAEFchVto,
                        voucherId: res.voucherId
                    }
                };
                return next;
            });

            notify.success(`${t('invoice_generated_success')}: ${voucherFormatted}`, t('success'));

            if (fullReservation?.id || reservation?.id) {
                const resId = Number(fullReservation?.id || reservation?.id);
                queryClient.invalidateQueries(reservationQueries.detail(resId));
            }

            return { success: true, voucherStr: voucherFormatted };
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || err.message || t('error');
            setGroups((prev) => {
                const next = [...prev];
                next[index] = {
                    ...next[index],
                    status: 'error',
                    error: errorMsg
                };
                return next;
            });
            notify.error(errorMsg, t('error'));
            return { success: false };
        }
    };

    // Generate all receipts sequentially
    const handleGenerateAll = async () => {
        setIsGeneratingAll(true);
        const generatedVouchers: string[] = [];

        for (let i = 0; i < groups.length; i++) {
            if (groups[i].status === 'success') {
                if (groups[i].result?.voucherNumber) {
                    generatedVouchers.push(`${String(groups[i].pos).padStart(4, '0')}-${String(groups[i].result!.voucherNumber).padStart(8, '0')}`);
                }
                continue;
            }
            const res = await generateSingleReceipt(i);
            if (res.success && res.voucherStr) {
                generatedVouchers.push(res.voucherStr);
            }
        }

        setIsGeneratingAll(false);

        if (fullReservation?.id || reservation?.id) {
            const resId = Number(fullReservation?.id || reservation?.id);
            queryClient.invalidateQueries(reservationQueries.detail(resId));
        }
    };

    const hasAnySuccess = groups.some((g) => g.status === 'success');
    const allSuccessful = groups.length > 0 && groups.every((g) => g.status === 'success');
    const totalReservationAmount = groups.reduce((sum, g) => {
        const amt = typeof g.totalAmount === 'number' ? g.totalAmount : (parseFloat(String(g.totalAmount)) || 0);
        return sum + amt;
    }, 0);

    return (
        <CardModal
            opened={opened}
            onClose={onClose}
            header={
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-teal-50 text-teal-700">
                            <IconReceiptTax size={20} stroke={1.8} />
                        </div>
                        <div>
                            <div className="font-bold text-sm text-gray-900">{t('generate_tax_receipts')}</div>
                            <div className="text-xxs text-gray-500 font-normal">
                                {t('reservation')} #{fullReservation?.id} &middot; {t('generate_tax_receipts_desc')}
                            </div>
                        </div>
                    </div>
                </div>
            }
            size="full"
        >
            <div className="space-y-5">
                {!isAfipConfigured && (
                    <Alert icon={<IconAlertCircle size={18} />} color="yellow" radius="md">
                        {t('afip_not_configured_alert')}
                    </Alert>
                )}

                {isLoadingReservation && (
                    <div className="flex items-center justify-center p-8 gap-3 text-sm text-gray-500">
                        <Loader size="sm" color="black" />
                        <span>{t('loading')}</span>
                    </div>
                )}

                {!isLoadingReservation && groups.length === 0 && (
                    <Alert icon={<IconAlertCircle size={18} />} color="blue" radius="md">
                        {t('no_billable_services')}
                    </Alert>
                )}

                {/* Receiver Info Section */}
                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/60 space-y-3">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        {t('receiver')}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <TextInput
                            label={t('name')}
                            value={receiverName}
                            onChange={(e) => setReceiverName(e.target.value)}
                            required
                            size="xs"
                        />
                        <Select
                            label={t('type')}
                            value={docTipo}
                            onChange={(val) => setDocTipo(val || '99')}
                            data={[
                                { value: '80', label: 'CUIT' },
                                { value: '86', label: 'CUIL' },
                                { value: '96', label: 'DNI' },
                                { value: '94', label: 'Pasaporte' },
                                { value: '99', label: 'Consumidor Final / Sin Identificar' }
                            ]}
                            size="xs"
                            required
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <TextInput
                            label={t('number')}
                            value={docNro}
                            onChange={(e) => setDocNro(e.target.value)}
                            disabled={docTipo === '99'}
                            required={docTipo !== '99'}
                            size="xs"
                        />
                        <Select
                            label={t('tax_condition')}
                            value={taxCondition}
                            onChange={(val) => setTaxCondition(val || '5')}
                            data={[
                                { value: '1', label: 'IVA Responsable Inscripto' },
                                { value: '4', label: 'IVA Sujeto Exento' },
                                { value: '5', label: 'Consumidor Final' },
                                { value: '6', label: 'Responsable Monotributo' },
                                { value: '8', label: 'Sujeto No Categorizado' },
                                { value: '9', label: 'Proveedor del Exterior' },
                                { value: '10', label: 'Cliente del Exterior' },
                                { value: '11', label: 'IVA Liberado - Ley Nº 19.640' },
                                { value: '13', label: 'Monotributista Social' },
                                { value: '15', label: 'IVA No Alcanzado' },
                                { value: '17', label: 'Monotributista Independiente Promovido' }
                            ]}
                            disabled={docTipo === '99'}
                            size="xs"
                        />
                        <Select
                            label={t('invoice_type')}
                            value={invoiceType}
                            onChange={(val) => handleUpdateInvoiceType(val || '6')}
                            data={[
                                { value: '1', label: 'Factura A' },
                                { value: '6', label: 'Factura B' },
                                { value: '11', label: 'Factura C' },
                                { value: '195', label: 'Factura T' }
                            ]}
                            size="xs"
                            required
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
                        <DatePickerCustom
                            label={t('service_period_start')}
                            value={fchServDesde ? dayjs(fchServDesde).format('YYYY-MM-DD') : ''}
                            onChange={(val) => setFchServDesde(val[0] ? new Date(val[0] + 'T00:00:00') : null)}
                            valueFormat="DD MMM YYYY"
                            clearable={false}
                        />
                        <DatePickerCustom
                            label={t('service_period_end')}
                            value={fchServHasta ? dayjs(fchServHasta).format('YYYY-MM-DD') : ''}
                            onChange={(val) => setFchServHasta(val[0] ? new Date(val[0] + 'T00:00:00') : null)}
                            valueFormat="DD MMM YYYY"
                            clearable={false}
                        />
                        <DatePickerCustom
                            label={t('due_date')}
                            value={fchVtoPago ? dayjs(fchVtoPago).format('YYYY-MM-DD') : ''}
                            onChange={(val) => setFchVtoPago(val[0] ? new Date(val[0] + 'T00:00:00') : null)}
                            valueFormat="DD MMM YYYY"
                            clearable={false}
                        />
                    </div>
                    <TextInput
                        label={t('invoice_description_label')}
                        placeholder={t('invoice_description_placeholder')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        size="xs"
                    />
                </div>

                {/* Grouped Receipts Section */}
                {groups.length > 0 && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                {t('grouped_receipts_to_generate')} ({groups.length})
                            </div>
                            <span className="text-xxs text-slate-500 font-medium">
                                {t('generate_tax_receipts_desc')}
                            </span>
                        </div>

                        <div className="space-y-3">
                            {groups.map((group, idx) => {
                                const isSuccess = group.status === 'success';
                                const isGenerating = group.status === 'generating';
                                const isError = group.status === 'error';
                                const modalFileName = fullReservation?.id
                                    ? (groups.length > 1
                                        ? `reserva-${fullReservation.id}-${group.pos}-${group.result?.voucherNumber}.pdf`
                                        : `reserva-${fullReservation.id}.pdf`)
                                    : `comprobante-${group.pos}-${group.result?.voucherNumber}.pdf`;

                                const downloadUrl = group.result?.voucherId
                                    ? `${import.meta.env.VITE_API_BASE_URL}/afip/vouchers/${group.result.voucherId}/download?filename=${encodeURIComponent(modalFileName)}`
                                    : null;

                                return (
                                    <div
                                        key={group.key + idx}
                                        className={`rounded-xl border p-4 transition-all ${isSuccess
                                            ? 'bg-emerald-50/50 border-emerald-300 ring-1 ring-emerald-500/20'
                                            : isError
                                                ? 'bg-red-50/50 border-red-300'
                                                : 'bg-white border-slate-200 shadow-2xs hover:border-slate-300'
                                            }`}
                                    >
                                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Badge color="blue" variant="filled" size="sm" radius="sm">
                                                    POS {group.pos}
                                                </Badge>
                                                <Badge color="violet" variant="light" size="sm" radius="sm">
                                                    {group.taxPercentage}% IVA
                                                </Badge>
                                                <span className="text-xs text-slate-500 font-medium">
                                                    ({group.bookings.length} {group.bookings.length === 1 ? t('service') : t('services')})
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                {isSuccess && group.result && (
                                                    <div className="flex items-center gap-2">
                                                        <Badge color="green" variant="filled" size="md" radius="sm" leftSection={<IconCheck size={14} />}>
                                                            {String(group.pos).padStart(4, '0')}-{String(group.result.voucherNumber).padStart(8, '0')}
                                                        </Badge>
                                                        {downloadUrl && (
                                                            <Button
                                                                size="xs"
                                                                variant="light"
                                                                color="green"
                                                                radius="xl"
                                                                leftSection={<IconDownload size={14} />}
                                                                onClick={() => window.open(downloadUrl, '_blank')}
                                                            >
                                                                {t('download_pdf')}
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="xs"
                                                            variant="light"
                                                            color="blue"
                                                            radius="xl"
                                                            leftSection={<IconMail size={14} />}
                                                            onClick={() => {
                                                                if (group.result?.voucherId) {
                                                                    setSelectedVoucherForEmail({
                                                                        id: group.result.voucherId,
                                                                        pos: group.pos,
                                                                        voucher_number: group.result.voucherNumber,
                                                                        cbte_tipo: parseInt(invoiceType, 10),
                                                                        receiver_name: receiverName,
                                                                        imp_total: Number(group.totalAmount)
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            {t('send_by_email')}
                                                        </Button>
                                                    </div>
                                                )}

                                                {!isSuccess && (
                                                    <Button
                                                        size="xs"
                                                        variant="subtle"
                                                        color="black"
                                                        radius="xl"
                                                        loading={isGenerating}
                                                        disabled={isGeneratingAll || !isAfipConfigured}
                                                        onClick={() => generateSingleReceipt(idx)}
                                                    >
                                                        {t('generate_receipt')}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Financial breakdown */}
                                        <div className="flex flex-wrap sm:flex-nowrap items-end gap-3 p-2.5 rounded-lg bg-slate-100/70 text-xs text-slate-700 mb-3">
                                            <div className="w-full sm:w-auto sm:flex-1 sm:min-w-0">
                                                <span className="text-xxs text-slate-500 block uppercase font-bold">{t('pos_label')}</span>
                                                <Select
                                                    size="xs"
                                                    value={String(group.pos)}
                                                    onChange={(val) => handleUpdateGroupPos(idx, val ? parseInt(val, 10) : null)}
                                                    data={availablePosOptions}
                                                    disabled={isSuccess || isGenerating}
                                                    className="mt-0.5 w-full"
                                                />
                                            </div>
                                            <div className="shrink-0">
                                                <span className="text-xxs text-slate-500 block uppercase font-bold">{t('iva_rate')}</span>
                                                <Select
                                                    size="xs"
                                                    value={group.ivaRate}
                                                    onChange={(val) => handleUpdateGroupIvaRate(idx, val || '5')}
                                                    data={[
                                                        { value: '5', label: '21%' },
                                                        { value: '4', label: '10.5%' },
                                                        { value: '6', label: '27%' },
                                                        { value: '3', label: '0%' }
                                                    ]}
                                                    disabled={isSuccess || isGenerating}
                                                    className="mt-0.5 w-20"
                                                />
                                            </div>
                                            <div className="shrink-0 min-w-[90px]">
                                                <span className="text-xxs text-slate-500 block uppercase font-bold">{t('tax_net_amount')}</span>
                                                <div className="font-semibold text-slate-900 mt-0.5 h-[30px] flex items-center">
                                                    $ {formatPrice(group.netAmount)}
                                                </div>
                                            </div>
                                            <div className="shrink-0 w-28 sm:w-36">
                                                <span className="text-xxs text-slate-500 block uppercase font-bold">{t('total_amount')}</span>
                                                <NumberInput
                                                    size="xs"
                                                    value={group.totalAmount}
                                                    onChange={(val) => handleUpdateGroupTotal(idx, val)}
                                                    min={0}
                                                    decimalScale={2}
                                                    thousandSeparator="."
                                                    decimalSeparator=","
                                                    prefix="$ "
                                                    hideControls
                                                    disabled={isSuccess || isGenerating}
                                                    className="mt-0.5 w-full"
                                                />
                                            </div>
                                        </div>

                                        {/* Bookings breakdown */}
                                        <div className="space-y-1">
                                            <div className="text-xxs font-bold uppercase text-slate-500 tracking-wider">
                                                {t('included_services')}:
                                            </div>
                                            <div className="divide-y divide-slate-100 text-xs">
                                                {group.bookings.map((b, bIdx) => {
                                                    const svcName = b.service?.name || b.service_data?.name || t('service');
                                                    const bDate = b.start_day ? formatDayShort(b.start_day) : null;
                                                    const bAmount = b.invoiceAmount !== undefined ? b.invoiceAmount : Number(b.money_in_amount || 0);

                                                    return (
                                                        <div key={b.id || bIdx} className="py-1.5 flex items-center justify-between text-slate-700 gap-3">
                                                            <div className="flex items-center gap-2 min-w-0">
                                                                <span className="font-medium truncate">{svcName}</span>
                                                                {bDate && (
                                                                    <span className="text-xxs text-slate-400 flex items-center gap-0.5 shrink-0">
                                                                        <IconCalendar size={12} /> {bDate}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-1.5 shrink-0">
                                                                <NumberInput
                                                                    size="xs"
                                                                    value={bAmount}
                                                                    onChange={(val) => handleUpdateBookingAmount(idx, bIdx, val)}
                                                                    min={0}
                                                                    decimalScale={2}
                                                                    thousandSeparator="."
                                                                    decimalSeparator=","
                                                                    prefix="$ "
                                                                    hideControls
                                                                    disabled={isSuccess || isGenerating}
                                                                    className="w-28 sm:w-32"
                                                                />
                                                                <span className="text-xxs text-slate-400 font-semibold">{b.money_in_currency || 'ARS'}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {isError && group.error && (
                                            <Alert color="red" radius="md" className="mt-2.5">
                                                {group.error}
                                            </Alert>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Footer Controls */}
                <Divider />
                <div className="flex flex-col sm:flex-row sm:items-center justify-end gap-3 pt-1">
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                        <Button
                            variant="subtle"
                            color="gray"
                            size="xs"
                            onClick={onClose}
                            disabled={isGeneratingAll}
                        >
                            {allSuccessful ? t('close') : t('cancel')}
                        </Button>

                        {!allSuccessful && groups.length > 0 && (
                            <Button
                                color="black"
                                size="xs"
                                radius="xl"
                                leftSection={<IconFileInvoice size={15} />}
                                loading={isGeneratingAll}
                                disabled={!isAfipConfigured || groups.length === 0}
                                onClick={handleGenerateAll}
                            >
                                {groups.length > 1 ? t('generate_all_receipts') : t('generate_receipt')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <SendAfipVoucherEmailModal
                opened={!!selectedVoucherForEmail}
                onClose={() => setSelectedVoucherForEmail(null)}
                voucher={selectedVoucherForEmail}
                business={fullReservation?.client_business}
            />
        </CardModal>
    );
}
